import { createSlice, type PayloadAction, createSelector } from '@reduxjs/toolkit'
import { type RootState } from '../index'

// Types
export interface WorkItem {
  id: string
  uid: string
  kind: 'upload' | 'extract' | 'validate' | 'annotate' | 'convert'
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  
  subject: {
    documentId?: string
    caseId?: string
    fileName?: string
    fileSize?: number
  }
  
  inputs: {
    fileUrl?: string
    schema?: string
    config?: Record<string, any>
  }
  
  outputs: {
    // Legacy fields (kept for backward compatibility)
    extractedDataUrl?: string
    annotationsDocId?: string
    validationReportId?: string
    pngFileUrl?: string
    extractedData?: any  // OLD: Full data (deprecated, too large for Firestore)
    
    // New Studio API fields (from Temporal workflows)
    // Summary data (small, stored in Firestore for quick access)
    extractionSummary?: {
      account_info?: {
        account_holder_name?: string
        account_number?: string
        bank_name?: string
      }
      statement_period?: {
        start_date?: string
        end_date?: string
        beginning_balance?: number
        ending_balance?: number
        total_deposits?: number
        total_withdrawals?: number
      }
      transaction_count?: number
      transaction_sample?: Array<{  // First 3 transactions only
        date?: string
        description?: string
        amount?: number
      }>
    }
    
    validationReport?: {
      isValid: boolean
      transactionCount: number
      accountCount: number
      statistics?: Record<string, any>
      correctness?: Record<string, any>
      completeness?: Record<string, any>
      balances_percentage_match?: Record<string, any>
      validation_results?: Record<string, any>
    }
    
    // GCS paths to full data (use storageService.ts to load)
    extractionResultPath?: string  // GCS path to FULL extraction JSON
    validationResultPath?: string  // GCS path to validation JSON
    
    // Download URLs
    excelUrl?: string  // Public URL for Excel download
    excelPath?: string  // GCS path for Excel file
    
    // Metadata
    confidence?: number
    evaluationScore?: number
    processingTimeMs?: number
    annotationCount?: number
    statistics?: Record<string, any>
    hasAnnotations?: boolean
    annotationsPath?: string  // Path to annotations subcollection
  }
  
  createdAt: number
  updatedAt: number
  completedAt?: number
  
  error?: {
    code: string
    message: string
    details?: any
  }
  
  provider?: {
    name: 'reducto' | 'cloudconvert' | 'internal'
    jobId?: string
    meta?: Record<string, any>
  }
}

export interface WorkRuntime {
  status: string
  progress: number
  message: string
  fileName?: string
  updatedAt: number
}

interface WorkState {
  // Normalized entities from Firestore
  byId: Record<string, WorkItem>
  ids: string[]
  
  // Runtime overlay from RTDB
  runtimeById: Record<string, WorkRuntime>
  
  // UI state
  filter: 'all' | 'active' | 'completed' | 'failed'
  selectedWorkId?: string
  
  // Loading states
  isLoading: boolean
  error: string | null
}

const initialState: WorkState = {
  byId: {},
  ids: [],
  runtimeById: {},
  filter: 'all',
  selectedWorkId: undefined,
  isLoading: false,
  error: null
}

const workSlice = createSlice({
  name: 'work',
  initialState,
  reducers: {
    // Firestore updates
    setWorkItems: (state, action: PayloadAction<WorkItem[]>) => {
      // Clear and rebuild normalized state
      state.byId = {}
      state.ids = []
      
      action.payload.forEach(item => {
        state.byId[item.id] = item
        if (!state.ids.includes(item.id)) {
          state.ids.push(item.id)
        }
      })
      
      // Sort by createdAt desc
      state.ids.sort((a, b) => {
        const itemA = state.byId[a]
        const itemB = state.byId[b]
        return (itemB?.createdAt || 0) - (itemA?.createdAt || 0)
      })
    },
    
    upsertWorkItem: (state, action: PayloadAction<WorkItem>) => {
      const item = action.payload
      state.byId[item.id] = item
      
      if (!state.ids.includes(item.id)) {
        state.ids.push(item.id)
        // Re-sort when adding new item
        state.ids.sort((a, b) => {
          const itemA = state.byId[a]
          const itemB = state.byId[b]
          return (itemB?.createdAt || 0) - (itemA?.createdAt || 0)
        })
      }
    },
    
    removeWorkItem: (state, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.byId[id]
      state.ids = state.ids.filter(itemId => itemId !== id)
      delete state.runtimeById[id]
    },
    
    // RTDB updates
    updateRuntime: (state, action: PayloadAction<{id: string, runtime: WorkRuntime}>) => {
      state.runtimeById[action.payload.id] = action.payload.runtime
    },
    
    updateMultipleRuntimes: (state, action: PayloadAction<Record<string, WorkRuntime>>) => {
      Object.entries(action.payload).forEach(([id, runtime]) => {
        state.runtimeById[id] = runtime
      })
    },
    
    clearRuntime: (state, action: PayloadAction<string>) => {
      delete state.runtimeById[action.payload]
    },
    
    clearAllRuntimes: (state) => {
      state.runtimeById = {}
    },
    
    // UI actions
    setFilter: (state, action: PayloadAction<WorkState['filter']>) => {
      state.filter = action.payload
    },
    
    selectWork: (state, action: PayloadAction<string | undefined>) => {
      state.selectedWorkId = action.payload
    },
    
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    }
  }
})

// Export actions
export const {
  setWorkItems,
  upsertWorkItem,
  removeWorkItem,
  updateRuntime,
  updateMultipleRuntimes,
  clearRuntime,
  clearAllRuntimes,
  setFilter,
  selectWork,
  setLoading,
  setError
} = workSlice.actions

// Export reducer
export default workSlice.reducer

// Selectors
export const selectWorkState = (state: RootState) => state.work

export const selectWorkById = (state: RootState, id: string) => 
  state.work.byId[id]

export const selectRuntimeById = (state: RootState, id: string) => 
  state.work.runtimeById[id]

export const selectMergedWork = (state: RootState, id: string) => {
  const work = state.work.byId[id]
  if (!work) return null
  
  return {
    ...work,
    runtime: state.work.runtimeById[id]
  }
}

// Get all work items with runtime merged
export const selectAllWork = createSelector(
  [selectWorkState],
  (work) => work.ids.map(id => ({
    ...work.byId[id],
    runtime: work.runtimeById[id]
  }))
)

// Get active (running/pending) work
export const selectActiveWork = createSelector(
  [selectAllWork],
  (items) => items.filter(w => 
    w.status === 'running' || w.status === 'pending'
  )
)

// Get filtered work based on UI filter
export const selectFilteredWork = createSelector(
  [selectAllWork, selectWorkState],
  (items, work) => {
    switch (work.filter) {
      case 'active':
        return items.filter(w => w.status === 'running' || w.status === 'pending')
      case 'completed':
        return items.filter(w => w.status === 'completed')
      case 'failed':
        return items.filter(w => w.status === 'failed')
      default:
        return items
    }
  }
)

// Get latest work for a specific document
export const selectLatestWorkForDocument = (
  state: RootState, 
  documentId: string, 
  kind?: WorkItem['kind']
) => {
  const works = Object.values(state.work.byId)
    .filter(w => {
      const matchesDoc = w.subject.documentId === documentId
      const matchesKind = !kind || w.kind === kind
      return matchesDoc && matchesKind
    })
    .sort((a, b) => b.createdAt - a.createdAt)
  
  if (works.length === 0) return null
  
  const latest = works[0]
  return {
    ...latest,
    runtime: state.work.runtimeById[latest.id]
  }
}

// Get work count by status
export const selectWorkCountByStatus = createSelector(
  [selectAllWork],
  (items) => {
    const counts = {
      pending: 0,
      running: 0,
      completed: 0,
      failed: 0,
      cancelled: 0,
      total: items.length
    }
    
    items.forEach(item => {
      counts[item.status]++
    })
    
    return counts
  }
)

// Check if any work is processing
export const selectIsAnyWorkProcessing = createSelector(
  [selectActiveWork],
  (activeItems) => activeItems.length > 0
)

// Get work for a specific case
export const selectWorkForCase = (state: RootState, caseId: string) => {
  return Object.values(state.work.byId)
    .filter(w => w.subject.caseId === caseId)
    .map(w => ({
      ...w,
      runtime: state.work.runtimeById[w.id]
    }))
    .sort((a, b) => b.createdAt - a.createdAt)
}
